// dependency-graph-ignore-file

import { parse, type ParseConfig, type ParseStepResult } from 'papaparse';

import { FilterLang } from './ParsingLangs/FilterLang';
import { LayoutLang } from './ParsingLangs/LayoutLang';
import { SortLang } from './ParsingLangs/SortLang';
import { createParsingMachine, isAnOutsideState } from './state-machine/state-machine';
import { parseEditMetadataRow } from './table-parsers/edit-metadata';
import {
  type AddFilterToken,
  type FieldToken,
  FILTERED_VALUE_KEYS,
  type LayoutToken,
  type OnTokenHandler,
  type ParsedRow,
  PARSER_STATES,
  type SortToken,
  type Token,
  TokenType,
  type ValueToken,
  type ViewToken,
} from './types';

/**
 * This class takes the raw output from the LLM and emits tokens
 */
export class StreamingCsvParser {
  onToken: OnTokenHandler;
  buffer = '';
  state: PARSER_STATES = PARSER_STATES.INIT;
  headerLine: string | null = null;
  handleStep: (row: ParsedRow) => void;
  parsingConfig: ParseConfig;
  operationType: 'create' | 'edit' = 'create';
  private stateMachine: ReturnType<typeof createParsingMachine>;

  constructor(onEmitToken: OnTokenHandler, operationType: 'create' | 'edit') {
    this.onToken = onEmitToken;
    this.handleStep = this.processRow.bind(this);
    this.parsingConfig = {
      header: true,
      dynamicTyping: (field: string | number) => {
        // Don't convert these identifier fields to numbers - keep them as strings
        // This prevents view/field names like '123' from becoming the number 123
        const stringFields = [
          'view_name',
          'field_name',
          'dependent_field',
          'target',
          'operation_type',
          'property',
        ];
        return !stringFields.includes(String(field));
      },
      skipEmptyLines: false,
      step: (results: ParseStepResult<ParsedRow>) => {
        this.handleStep(results.data);
      },
    };
    this.operationType = operationType;
    this.stateMachine = createParsingMachine();
  }

  private createSortTokens(row: ParsedRow): SortToken[] {
    if (!row['sorts']) {
      return [];
    }

    const viewName = row['view_name'];

    try {
      const tokens: Omit<SortToken, 'viewName'>[] = SortLang.Statement.tryParse(
        row['sorts']
      );

      return tokens.map((token) => ({
        ...token,
        viewName,
      }));
    } catch (error) {
      console.error('Error creating sort tokens', error);
      return [];
    }
  }

  private createFilterTokens(row: ParsedRow): AddFilterToken[] {
    if (!row['filters']) {
      return [];
    }

    const viewName = row['view_name'];

    try {
      const tokens: Omit<AddFilterToken, 'viewName'>[] = [
        FilterLang.Statement.tryParse(row['filters']),
      ];

      return tokens.map((token) => ({
        ...token,
        viewName,
      }));
    } catch (error) {
      console.error('Error creating filter tokens', error);
      return [];
    }
  }

  private mapFieldsOperation(row: ParsedRow): Token[] {
    return [this.createFieldToken(row)];
  }

  private mapViewsOperation(row: ParsedRow): Token[] {
    const tokens: Token[] = [this.createViewToken(row)];

    const layoutToken = this.createLayoutToken(row);
    if (layoutToken) {
      tokens.push(layoutToken);
    }

    const sortTokens = this.createSortTokens(row);
    for (const sortToken of sortTokens) {
      tokens.push(sortToken);
    }

    const filterTokens = this.createFilterTokens(row);
    for (const filterToken of filterTokens) {
      tokens.push(filterToken);
    }

    return tokens;
  }

  private mapEntriesOperation(row: ParsedRow): Token[] {
    const tokens: Token[] = [
      {
        tokenType: TokenType.ADD_ENTRY,
      },
    ];

    const valueTokens = this.createValueTokens(row);
    for (const valueToken of valueTokens) {
      tokens.push(valueToken);
    }

    return tokens;
  }

  private mapDataOperation(row: ParsedRow): Token[] {
    const tokens: Token[] = [];

    switch (row['operation_type']) {
      case 'add_entry':
        tokens.push({
          tokenType: TokenType.ADD_ENTRY,
        });
        break;
      case 'edit_entry':
        tokens.push({
          tokenType: TokenType.EDIT_ENTRY,
          rowNumber: row['entry_index'],
        });
        break;
      case 'delete_entry':
        tokens.push({
          tokenType: TokenType.REMOVE_ENTRY,
          rowNumber: row['entry_index'],
        });
        return tokens;
    }

    const valueTokens = this.createValueTokens(row, true);
    for (const valueToken of valueTokens) {
      tokens.push(valueToken);
    }

    return tokens;
  }

  processRow(row: ParsedRow) {
    let tokens: Token[] = [];

    if (this.stateMachine.state() === 'create_fields') {
      tokens = this.mapFieldsOperation(row);
    } else if (this.stateMachine.state() === 'create_views') {
      tokens = this.mapViewsOperation(row);
    } else if (this.stateMachine.state() === 'create_data') {
      tokens = this.mapEntriesOperation(row);
    } else if (this.stateMachine.state() === 'edit_meta') {
      tokens = [...parseEditMetadataRow(row)];
    } else if (this.stateMachine.state() === 'edit_data') {
      tokens = this.mapDataOperation(row);
    }

    for (const token of tokens) {
      this.onToken(token);
    }
  }

  private createValueTokens(row: ParsedRow, resetValue = false) {
    const valueTokens: ValueToken[] = [];
    for (const [key, value] of Object.entries(row)) {
      if (FILTERED_VALUE_KEYS.includes(key as any)) {
        continue;
      }

      valueTokens.push({
        tokenType: TokenType.VALUE,
        fieldName: key,
        value,
        resetValue,
      });
    }

    return valueTokens;
  }

  private createLayoutToken(row: ParsedRow): LayoutToken | undefined {
    if (!row['layout']) {
      return;
    }

    const viewName = row['view_name'];

    try {
      const token: Omit<LayoutToken, 'viewName'> = LayoutLang.Statement.tryParse(
        row['layout']
      );

      return {
        ...token, // the array will only be one element
        viewName,
      };
    } catch (error) {
      console.error('Error creating layout tokens', error);
      return undefined;
    }
  }

  private createViewToken(row: ParsedRow) {
    const viewToken: ViewToken = {
      tokenType: TokenType.VIEW,
      name: row['view_name'],
    };
    return viewToken;
  }

  private createFieldToken(row: ParsedRow) {
    const token: FieldToken = {
      tokenType: TokenType.FIELD,
      name: row['field_name'],
      type: row['type'],
    };

    if (row.dependent_field) {
      token.dependentField = row['dependent_field'];
    }

    return token;
  }

  public static isXMLOpeningTag(line: string): boolean {
    return /^<[^/][^>]+>$/.test(line);
  }

  public static isXMLTag(line: string, tagName: string): boolean {
    return new RegExp(`^<${tagName}>$`).test(line);
  }

  public static isXMLClosingTag(line: string): boolean {
    return /^<\/[^>]+>$/.test(line);
  }

  onReceiveChunk(chunk: string) {
    this.buffer += chunk;

    const lines = this.buffer.split('\n');
    this.buffer = lines.pop() || '';

    for (const line of lines) {
      if (StreamingCsvParser.isXMLTag(line, 'fields')) {
        this.stateMachine.send({ type: 'enteringFieldsTag' });
      } else if (StreamingCsvParser.isXMLTag(line, 'metadata')) {
        this.stateMachine.send({ type: 'enteringMetaTag' });
      }

      if (StreamingCsvParser.isXMLOpeningTag(line)) {
        this.stateMachine.send({ type: 'openingBracket' });
        this.headerLine = null;
        continue;
      }

      if (StreamingCsvParser.isXMLClosingTag(line)) {
        this.stateMachine.send({ type: 'closingBracket' });
        continue;
      }

      if (isAnOutsideState(this.stateMachine.state())) {
        // do not consume lines not wrapped in XML
        continue;
      }

      let isCurrentLineTheHeader = !this.headerLine;

      if (isCurrentLineTheHeader) {
        const expectedHeaderLine = this.getExpectedHeaderLine();
        if (expectedHeaderLine && !this.isValidHeaderLine(line, expectedHeaderLine)) {
          isCurrentLineTheHeader = false;
          this.headerLine = expectedHeaderLine;
        } else {
          this.headerLine = line;
          continue;
        }
      }

      if (!isCurrentLineTheHeader) {
        const stringToParse = [...(this.headerLine ? [this.headerLine] : []), line].join(
          '\n'
        );
        parse(stringToParse, this.parsingConfig);
      }
    }
  }

  private isValidHeaderLine(line: string, expectedHeaderLine: string): boolean {
    if (line === expectedHeaderLine) {
      return true;
    }

    if (this.stateMachine.state() === 'create_fields') {
      const normalizedLine = line.replace(/,+$/, '');
      return (
        normalizedLine === 'field_name,type' ||
        normalizedLine === 'field_name,type,dependent_field'
      );
    }

    return false;
  }

  private getExpectedHeaderLine(): string | undefined {
    switch (this.stateMachine.state()) {
      case 'create_fields':
        return 'field_name,type,dependent_field';
      case 'create_views':
        return 'view_name,layout,filters,sorts';
      case 'edit_meta':
        return 'operation_type,target,property,value';
    }
  }
}
